import { PdpScoreStatus, type ScoreStatus } from '@vizit/api';
import { Link, ScorePill, ScoreSticker, ScoreStickerSize, Text, TextSize } from '@vizit/atoms';
import { Skeleton } from '@vizit/atoms';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { Heading, HeadingVariant } from '@vizit/molecules';
import { cx } from '@vizit/theme';

interface ScoreHeaderProps {
  /**
   * The score of the asset
   */
  score?: number | null;
  /**
   * The score status of the asset
   */
  scoreStatus: ScoreStatus | null;
  /**
   * Whether the score is omitted
   */
  omitScore?: boolean | null;
  /**
   * The category of the asset
   */
  category: string | null;
  /**
   * The type of the asset
   */
  assetType: 'hero' | 'carousel' | null;
  /**
   * The classification label (shown when feature flag is enabled)
   */
  classification?: string | null;
  /**
   * The loading state of the asset score
   */
  isLoading: boolean;
}

function ScoreHeader(props: ScoreHeaderProps) {
  const {
    score = null,
    omitScore = false,
    scoreStatus,
    category,
    assetType: assetTypeProp,
    classification,
    isLoading,
  } = props;

  const showClassification = useFeatureFlag(FEATURE_FLAGS.SHOW_IMAGE_CLASSIFICATION, false);

  const isHero = assetTypeProp === 'hero';
  const assetType = assetTypeProp ? assetTypeProp.charAt(0).toUpperCase() + assetTypeProp.slice(1) : undefined;
  const displayedType = showClassification ? (classification ?? assetType) : assetType;
  const scoreLabel = isHero ? 'Vizit Hero Score' : 'Vizit Image Score';
  const assetTypeLabel = isHero ? 'hero' : 'carousel';

  const isScoreOmitted = !!omitScore;
  const isScoreAvailable = !isScoreOmitted && score !== null;
  const isScoreLoading = isLoading || scoreStatus !== PdpScoreStatus.Scored;
  const isScoreBroken = !isScoreAvailable && !isScoreOmitted && !isScoreLoading;

  return (
    <div className="h-44 w-full flex items-center max-md:h-full">
      <div className="flex flex-row gap-16 px-16 py-2 [--heading-height:24px] [--text-height:20px] max-lg:px-8 max-lg:py-2 max-md:gap-7">
        <div className="flex flex-row gap-8 items-center max-md:gap-7">
          <ScoreSticker score={isScoreAvailable ? score : null} size={ScoreStickerSize.Sm} isLoading={isScoreLoading} />
          <div className="flex flex-col gap-2 w-full max-md:hidden">
            <div className="flex flex-row gap-4 items-start max-md:flex-wrap">
              <Heading variant={HeadingVariant.Eyebrow} className="font-semibold leading-[var(--heading-height)]">
                {scoreLabel}
              </Heading>
              <ScorePill score={isScoreAvailable ? score : null} isLoading={isScoreLoading} />
            </div>
            {isScoreLoading && <Skeleton className="w-[408px] inline-block h-[var(--text-height)]" />}
            {isScoreAvailable && (
              <Text maxLines={2} size={TextSize.Sm} className="leading-[var(--text-height)]">
                This image is likely to <span className="font-medium">convert better than {score}%</span> of other
                <span className="ml-1">{assetTypeLabel}</span> images in the same category.
              </Text>
            )}
            {isScoreBroken && (
              <Text maxLines={2} size={TextSize.Sm} className="leading-[var(--text-height)]">
                Cannot score this image at this time. Please try again later.
              </Text>
            )}
            {isScoreOmitted && (
              <Text maxLines={2} size={TextSize.Sm} className="leading-[var(--text-height)]">
                This image cannot be confidently scored at this time. Please read why{' '}
                <Link
                  className="text-sm"
                  href="https://vizit.helpscoutdocs.com/article/73-why-some-images-are-not-scored"
                  target="_blank"
                >
                  here
                </Link>
                .
              </Text>
            )}
          </div>
        </div>
        <div className="flex flex-row gap-16 self-center max-md:gap-7">
          <div className="flex flex-col gap-2 items-start">
            <Heading variant={HeadingVariant.Eyebrow} className="font-semibold leading-[var(--heading-height)]">
              Image type
            </Heading>
            {isLoading ? (
              <Skeleton className="w-full h-[var(--text-height)]" />
            ) : (
              <Text
                size={TextSize.Sm}
                className={cx('leading-[var(--text-height)]', !displayedType && 'text-center text-tertiary')}
              >
                {displayedType ?? '-'}
              </Text>
            )}
          </div>
          <div className="flex flex-col gap-2 items-start">
            <Heading variant={HeadingVariant.Eyebrow} className="font-semibold leading-[var(--heading-height)]">
              Category
            </Heading>
            {isLoading ? (
              <Skeleton className="w-full h-[var(--text-height)]" />
            ) : (
              <Text
                size={TextSize.Sm}
                className={cx('leading-[var(--text-height)]', !category && 'text-center text-tertiary')}
              >
                {category ?? '-'}
              </Text>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { ScoreHeader };
